/**
 * Balanço de tipos de veículos da unidade aplicado à frota da roteirização operacional.
 *
 * O cadastro da unidade guarda um percentual por TIPO BASE (tipos_veiculos). O seletor
 * operacional, porém, lista variantes tipo × lotação (id = tipo * 10000 + lotação). Esta função
 * devolve o `sharePercent` de cada variante selecionada, na ordem recebida:
 *
 *  1. Um percentual digitado na tela para a variante (`overrides[key]`, key = id da variante)
 *     vale como está.
 *  2. Sem ele, vale o percentual do tipo no balanço da unidade (`percentual_balanco`); as
 *     variantes selecionadas de um mesmo tipo dividem esse percentual igualmente.
 *  3. Tipo sem percentual cadastrado fica com 0.
 *
 * NÃO renormaliza: quem confere a soma é a tela, que bloqueia a roteirização quando os tipos
 * selecionados não somam 100% (`somaSharePercent` / `shareSomaOk`), e o solver, que recusa
 * `sharePercent` que não soma 100. Tipo com 0% não é ofertado: o solver o tira da frota.
 *
 * A divisão entre variantes é em centésimos de ponto percentual, inteira, com o resto no
 * último, para não perder centésimos.
 *
 * ATENÇÃO: tem espelho em Python em
 * monitor/scheduler/pythonScripts/verso/fleet/balanco.py — mudou aqui, muda lá.
 *
 * CommonJS de propósito: a tela importa via webpack e os testes Node fazem require.
 */
'use strict';

const SOMA_TOLERANCIA = 0.005;

/** Id do tipo base de uma linha do seletor operacional (variantes são tipo * 10000 + lotação). */
function tipoBaseDaLinha(row) {
    const base = Number(row && row.id_tipo_veiculo_base);
    if (Number.isFinite(base) && base > 0) return base;
    const id = Number(row && row.id_tipo_veiculo);
    if (!Number.isFinite(id) || id <= 0) return null;
    return id >= 10000 ? Math.floor(id / 10000) : id;
}

/** Percentual do cadastro como número ≥ 0; ausente/inválido vira 0. */
function percentualDaLinha(row) {
    const raw = row && row.percentual_balanco;
    if (raw === null || raw === undefined || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Percentual digitado: número finito em [0, 100], ou null quando vazio/inválido. */
function percentualDigitado(valor) {
    if (valor === null || valor === undefined || valor === '') return null;
    const n = Number(valor);
    return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
}

/**
 * @param {Array<object>} rows linhas selecionadas (operationalVehicleTypeRows), na ordem da frota
 * @param {object} [overrides] percentual digitado por id da variante (string)
 * @returns {Array<number>} sharePercent por linha (mesma ordem); digitado inválido vira 0
 */
function calcularSharePercentOperacional(rows, overrides) {
    const lista = Array.isArray(rows) ? rows : [];
    const digitados = overrides && typeof overrides === 'object' ? overrides : {};

    const indicesPorTipo = new Map();
    lista.forEach((row, idx) => {
        const tipo = tipoBaseDaLinha(row);
        if (tipo == null) return;
        if (!indicesPorTipo.has(tipo)) indicesPorTipo.set(tipo, []);
        indicesPorTipo.get(tipo).push(idx);
    });

    const saida = lista.map(() => 0);
    indicesPorTipo.forEach((indices) => {
        const centesimos = Math.round(percentualDaLinha(lista[indices[0]]) * 100);
        const parte = Math.floor(centesimos / indices.length);
        indices.forEach((idx, j) => {
            const ultima = j === indices.length - 1;
            saida[idx] = (ultima ? centesimos - parte * (indices.length - 1) : parte) / 100;
        });
    });

    lista.forEach((row, idx) => {
        const key = String(row && row.id_tipo_veiculo);
        if (Object.prototype.hasOwnProperty.call(digitados, key)) {
            const v = percentualDigitado(digitados[key]);
            saida[idx] = v == null ? 0 : v;
        }
    });
    return saida;
}

/** Soma dos percentuais em 2 casas. */
function somaSharePercent(shares) {
    const total = (Array.isArray(shares) ? shares : []).reduce((acc, v) => {
        const n = Number(v);
        return acc + (Number.isFinite(n) ? n : 0);
    }, 0);
    return Math.round(total * 100) / 100;
}

/** Cada percentual em [0, 100] e soma igual a 100 (lista vazia não passa). */
function shareSomaOk(shares) {
    const lista = Array.isArray(shares) ? shares : [];
    if (!lista.length) return false;
    const cadaValido = lista.every((v) => {
        const n = Number(v);
        return v !== '' && v != null && Number.isFinite(n) && n >= 0 && n <= 100;
    });
    return cadaValido && Math.abs(somaSharePercent(lista) - 100) < SOMA_TOLERANCIA;
}

module.exports = {
    calcularSharePercentOperacional,
    somaSharePercent,
    shareSomaOk,
    tipoBaseDaLinha,
};
